import { observable } from "mobx";
import YouTubeRepository from "../repository/YouTubeRepository";
import NestStore from "./NestStore";
import Job from "../models/Job";
import YoutubeAuthentication from "../models/YoutubeAuthentication";

class YouTubeStore extends NestStore {
  /**
   * @type {YouTubeRepository}
   */
  youTubeRepository;

  /**
   * @type {YoutubeAuthentication}
   */
  @observable youtubeAuthentication;

  /**
   * @type {Job}
   */
  @observable uploadJob;

  @observable jobStatusPulling = false;

  constructor() {
    super();
    this.youTubeRepository = new YouTubeRepository();
  }

  /**
   * Get Youtube user info
   * @returns {Promise<any> | undefined}
   */
  getUserInfo() {
    this.isLoading = true;
    return this.youTubeRepository
      .getUserInfo()
      .then(res => {
        this.isLoading = false;
        if (res.status === "success" && res.data.youTubeAuthentication) {
          this.youtubeAuthentication = new YoutubeAuthentication(res.data.youTubeAuthentication);
          if (!this.youtubeAuthentication.apiSyncedDts) {
            this.updateYoutubeData();
          }
        }
      })
      .catch(e => {
        this.isLoading = false;
        this.setErrorMessages(e);
        throw e;
      });
  }

  updateYoutubeData() {
    return this.youTubeRepository
      .fetchAccountData(this.youtubeAuthentication.youtubeAuthenticationId)
      .then(res => {
        if (res.status === "success" && res.data.youTubeAuthentication) {
          this.youtubeAuthentication = new YoutubeAuthentication(res.data.youTubeAuthentication);
        }
      })
      .catch(e => {
        this.setErrorMessages(e);
        throw e;
      });
  }

  /**
   * Generate an authentication link to Youtube
   * @returns {Promise<any> | undefined}
   */
  getLoginUrl() {
    return this.youTubeRepository
      .authorizationUrls()
      .then(res => {
        if (res.status === "success") {
          return res.data.youTubeAuthorizationUrl.url;
        }
        return null;
      })
      .catch(e => {
        this.setErrorMessages(e);
        throw e;
      });
  }

  /**
   * Remove link from Youtube account
   * @returns {Promise<any> | undefined}
   */
  removeAccountLink() {
    return this.youTubeRepository
      .removeAccountLink(this.youtubeAuthentication.youtubeAuthenticationId)
      .then(res => {
        if (res.status === "success") {
          this.youtubeAuthentication = null;
        } else {
          this.errorMessage = res.errorMessage;
        }
      })
      .catch(e => {
        this.setErrorMessages(e);
        throw e;
      });
  }

  /**
   * Generate an upload Youtube job
   * @param tourId
   * @returns {Promise<any> | undefined}
   */
  uploadVideo(tourId) {
    return this.youTubeRepository
      .createUploadJob(tourId)
      .then(res => {
        if (res.status === "success") {
          this.uploadJob = new Job(res.data.youTubeUploadJob);
          this.startPulling();
        } else {
          this.errorMessage = res.message;
        }
      })
      .catch(e => {
        this.setErrorMessages(e);
        throw e;
      });
  }

  /**
   * Check if a Youtube upload job exists
   * @param tourId
   * @returns {Promise<any> | undefined}
   */
  getUploadJobId(tourId) {
    return this.youTubeRepository
      .findUploadJob(tourId)
      .then(res => {
        if (res.status === "success" && res.data.youTubeUploadJobs.length) {
          this.uploadJob = new Job(res.data.youTubeUploadJobs[0]);
        } else {
          this.errorMessage = res.message;
        }
      })
      .catch(e => {
        this.setErrorMessages(e);
        throw e;
      });
  }

  /**
   * Update Youtube Upload job information
   * @param startPulling
   * @returns {Promise<never>|Promise<any>|undefined}
   */
  getUploadStatus(startPulling = false) {
    if (!this.uploadJob) {
      return Promise.reject(new Error("No job found"));
    }
    return this.youTubeRepository
      .getUploadJobStatus(this.uploadJob.jobId)
      .then(res => {
        if (res.status === "success") {
          this.uploadJob = new Job(res.data.youTubeUploadJob);
          if (startPulling) {
            this.startPulling();
          }
          if (!this.uploadJob.isRunning) {
            this.stopPulling();
          }
        } else {
          this.errorMessage = res.message;
        }
      })
      .catch(this.setErrorMessages);
  }

  /**
   * Start Pulling task if not already running
   */
  startPulling() {
    if (!this.jobStatusPulling) {
      this.jobStatusPulling = true;
      this.pull();
    }
  }

  /**
   * Update Youtube upload job information each 5sec
   */
  pull() {
    setTimeout(() => {
      if (!this.jobStatusPulling) {
        return;
      }
      this.getUploadStatus(false)
        .then(() => this.pull())
        .catch(() => this.stopPulling());
    }, 5000);
  }

  /**
   * Stop the Pulling task
   */
  stopPulling() {
    this.jobStatusPulling = false;
  }
}
export default YouTubeStore;
